import React, { useEffect, useMemo, useRef, useState } from "react";
import { CustomDialog } from "../../../components/StyledComponents";
import { DialogActions, DialogContent, DialogTitle } from "@mui/material";
import {
  ControlpanelPrimaryButton,
  ControlpanelSecondaryButton,
  GrayIconButton,
} from "../../../components/StyledButton";
import CloseIcon from "@mui/icons-material/Close";
import { useTranslation } from "react-i18next";
import CustomInput from "../../../components/CustomInput";
import my_company_logo from "../../../assets/images/logo/my-company-logo.png";
import { useLoading } from "../../../context/LoadingContext";
import { toast } from "react-toastify";
import { updateGroup } from "../../../axios/Group/GroupUrlApi";
import EditIcon16pxSvgNew from "../../../assets/images/icons/EditIcon16pxSvgNew";

const EditUserGroupDialog = ({
  onClose,
  handleRefreshTable,
  info,
  getStatusAddNewGroup,
}) => {
  const { t } = useTranslation();
  const [show, hide] = useLoading();
  const anchorRefAdvanced = useRef(null);

  const handleCloseDialog = () => {
    onClose();
  };

  const [isNewImageSelected, setIsNewImageSelected] = useState(false);

  const imageInputRef = useRef(null);

  const handleIconClick = () => {
    imageInputRef.current.click();
  };

  const handleImageChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setIsNewImageSelected(true);
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result;
        setPrevImageUrl(base64String);
      };
      reader.readAsDataURL(file);
    }
  };

  const [prevImageUrl, setPrevImageUrl] = useState(my_company_logo);

  useEffect(() => {
    const logo = info?.profilePicture;

    // Regex check xem có phải base64 hình ảnh không
    const isBase64Image = /^([A-Za-z0-9+/=])+$/i.test(logo);

    setPrevImageUrl(
      logo && isBase64Image ? `data:image/png;base64,${logo}` : my_company_logo
    );
  }, [info]);

  const [formData, setFormData] = useState({
    groupCode: info?.groupName,
    groupDescription: info?.remark,
  });

  const originalRef = useRef({
    groupCode: info?.groupName ?? "",
    groupDescription: info?.remark ?? "",
    profilePicture: info?.profilePicture ?? "",
  });

  const getBase64Payload = (s) => {
    if (!s) return "";
    return s.startsWith("data:") ? s.split(",")[1] || "" : s;
  };

  const imageChanged = useMemo(() => {
    const current = getBase64Payload(prevImageUrl);
    const original = getBase64Payload(originalRef.current.profilePicture);
    // Nếu ảnh gốc rỗng thì coi my_company_logo là "không có ảnh"
    const treatAsEmpty = (x) => !x || x === getBase64Payload(my_company_logo);
    if (treatAsEmpty(current) && treatAsEmpty(original)) return false;
    return current !== original;
  }, [prevImageUrl]);

  const [hasChanges, setHasChanges] = useState(false);
  useEffect(() => {
    const current = {
      groupCode: formData.groupCode ?? "",
      groupDescription: formData.groupDescription ?? "",
    };
    const original = {
      groupCode: originalRef.current.groupCode ?? "",
      groupDescription: originalRef.current.groupDescription ?? "",
    };
    const formChanged = JSON.stringify(current) !== JSON.stringify(original);
    setHasChanges(formChanged || imageChanged);
  }, [formData, imageChanged]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      show();

      let logo = null;
      if (imageChanged) {
        // gửi base64 payload (không gồm prefix dataURL)
        logo = getBase64Payload(prevImageUrl);
      }

      const payload = {
        group: info?.id,
        profilePicture: logo,              // null nếu không đổi
        remark: formData.groupDescription,
        groupName: formData.groupCode,
        langId: localStorage.getItem("i18nextLngId"),
      };

      const response = await updateGroup(payload);
      if (response.data.responseCode === 0) {
        toast.success(t('common.edit-group-successfully'));
        getStatusAddNewGroup(true);
        handleRefreshTable();
        onClose();
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      if (error?.response && error.response.status !== 401) {
        toast.error(t(error.response.data));
      } else {
        toast.error(t('common.network-timeout'));
      }
    } finally {
      hide();
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    const limits = {
      groupCode: 64,
      groupDescription: 128,
    };

    const max = limits[name] ?? Infinity;
    const clipped = value.length > max ? value.slice(0, max) : value;
    setFormData((prevData) => ({
      ...prevData,
      [name]: clipped,
    }));
  };

  return (
    <CustomDialog
      maxWidth={"xl"}
      open
      onClose={handleCloseDialog}
      keepMounted
      transitionDuration={window.__tourIsOpen ? 0 : undefined}
      data-tour="user-group-edit-dialog"
    >
      <DialogTitle>
        <div className="flex justify-between">
          <div className="flex items-center gap-4">
            <p className="text-xl font-semibold ">
              {t("common.edit-user-group")}
            </p>
          </div>
          <GrayIconButton onClick={handleCloseDialog}>
            <CloseIcon />
          </GrayIconButton>
        </div>
      </DialogTitle>
      <DialogContent>
        <div className="pt-5" data-tour="user-group-edit-dialog-input">
          <div className="flex flex-col">
            <div className="flex items-center justify-between">
              <input
                type="file"
                ref={imageInputRef}
                className="hidden"
                onChange={handleImageChange}
                accept="image/*"
              />
            </div>

            <div className="flex items-center justify-center overflow-hidden p-[5px] rounded-[4px] bg-white">
              <div className="relative p-1 border-[1px] border-[#E5E7EB] rounded inline-block self-end">
                <img
                  src={prevImageUrl}
                  alt="picture_profile"
                  className="h-[170px] w-[168px]"
                />
                <button
                  ref={anchorRefAdvanced}
                  onClick={handleIconClick}
                  className="
                    absolute
                    bottom-0
                    right-0
                    w-10
                    h-10
                    rounded-xl
                    bg-[#0057FF]
                    hover:bg-[#00308C]
                    flex
                    items-center
                    justify-center
                    shadow-lg
                    transition
                  "
                >
                  <EditIcon16pxSvgNew />
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-x-10 gap-y-3 relative">
              <div>
                <CustomInput
                  required={true}
                  title={t("common.group-code")}
                  inputType="text"
                  name="groupCode"
                  id="editGroupCode"
                  placeholder={t("common.group-code")}
                  handleInputChange={handleInputChange}
                  value={formData.groupCode}
                  inputProps={{ maxLength: 64 }}
                />
                {/* <p className="text-sm text-gray-500 text-right mt-1">
                  {(formData.groupCode ?? "").length}/64
                </p> */}
              </div>
              <div>
                <CustomInput
                  title={t("common.description")}
                  inputType="text"
                  name="groupDescription"
                  id="editGroupDescription"
                  placeholder={t("common.description")}
                  handleInputChange={handleInputChange}
                  value={formData.groupDescription}
                  inputProps={{ maxLength: 128 }}
                />
                {/* <p className="text-sm text-gray-500 text-right mt-1">
                  {(formData.groupDescription ?? "").length}/128
                </p> */}
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
      <DialogActions>
        <div className="flex gap-2 px-4 py-4">
          <ControlpanelSecondaryButton onClick={handleCloseDialog}>
            <p>{t("button.close")}</p>
          </ControlpanelSecondaryButton>
          <div data-tour="user-group-edit-dialog-save">
            <ControlpanelPrimaryButton
              disabled={(!hasChanges || !formData.groupCode) && !window.__tourIsOpen}
              onClick={handleSubmit}
            >
              <p>{t("button.save")}</p>
            </ControlpanelPrimaryButton>
          </div>
        </div>
      </DialogActions>
    </CustomDialog>
  );
};

export default EditUserGroupDialog;
